import React, { useState, useEffect, useRef } from 'react';
import ProgressBar from './progressBar';
import './styles.css';

// In production, this will be an empty string because the server is hosted on the same domain
const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

function App() {
  const CompleteMessage =
    "Your file has been converted.<br/> We've emailed you a download link. The link will be available for 7 days.";
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const [taskStatus, setTaskStatus] = useState({ percentage: 0, message: 'Pending' });
  const [fileStatus, setFileStatus] = useState('');
  const [mainTaskId, setMainTaskId] = useState('');
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const emailInputRef = useRef(null);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const emailError = emailTouched
    ? email.trim() === ''
      ? 'Email is required to receive the download link.'
      : !isValidEmail(email)
        ? 'Enter a valid email address.'
        : ''
    : '';

  function getDisplayStatus(status) {
    switch (status) {
      case 'Pending':
        return 'Pending…';
      case 'In Progress':
        return 'Converting…';
      case 'Completed':
        return 'Completed';
      case 'Failed':
        return 'Failed';
      default:
        return status || '';
    }
  }

  async function statusCheck(taskId = mainTaskId) {
    try {
      const response = await fetch(`${SERVER_URL}/api/acesconverter/status?taskId=${taskId}`, {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();

        if (taskId === mainTaskId) {
          setTaskStatus({
            percentage: data.percentage,
            message: data.status,
          });
          if (data.status === 'Completed') {
            setFileStatus(CompleteMessage);
          } else if (data.status === 'Failed') {
            const fallbackError =
              "We couldn't process your file. Please make sure it's a valid ACES XML and try again.";
            setFileStatus(data.result || fallbackError);
          }
        }
        return true;
      } else if (response.status === 404) {
        return false;
      }
    } catch (error) {
      setFileStatus('Something went wrong. Please try again.');
      return false;
    }
  }

  useEffect(() => {
    let isMounted = true;
    const checkStatus = async () => {
      if (taskStatus.message !== 'Completed' && taskStatus.message !== 'Failed') {
        await statusCheck();
      }
      if (isMounted) {
        setTimeout(checkStatus, 1000);
      }
    };

    if (mainTaskId) {
      checkStatus();
    }

    return () => {
      isMounted = false;
    };
    // here and below we cannot add suggested dependencies
    // as it provokes endless statusCheck calling
    // eslint-disable-next-line
  }, [mainTaskId, taskStatus.message]);

  const handleFileChange = (event) => {
    if (event.target.files[0] && isFileFine(event.target.files[0])) {
      setFile(event.target.files[0]);
      setFileName(event.target.files[0].name);
    } else {
      setFileStatus('No file selected. Please choose an ACES XML file (.xml).');
      setFileName('');
    }
  };

  function isFileFine(file) {
    if (!file) {
      setFileStatus('No file selected. Please choose an ACES XML file (.xml).');
      return false;
    }
    if (file.name.split('.').pop() !== 'xml') {
      setFileStatus('Please upload an ACES XML file (.xml).');
      return false;
    }
    return true;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFileStatus('Uploading…');

    if (!file) {
      setFileStatus('No file selected. Please choose an ACES XML file (.xml).');
      return;
    }

    const emailValue = email.trim();
    if (!emailValue || !isValidEmail(emailValue)) {
      setEmailTouched(true);
      setFileStatus('Enter your email address to receive the download link.');
      if (emailInputRef.current) {
        emailInputRef.current.focus();
      }
      return;
    }

    if (!isSubscribed) {
      setFileStatus('To continue, confirm you have an active AutoCare subscription.');
      return;
    }

    const formData = new FormData(event.target);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);

      const response = await fetch(`${SERVER_URL}/api/acesconverter/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const statusReturned = await response.json();
        setMainTaskId(statusReturned.taskId);
        setTaskStatus({
          percentage: statusReturned.taskInfo.percentage,
          message: statusReturned.taskInfo.status || 'Pending',
        });
        if (statusReturned.taskInfo.result) {
          setFileStatus(statusReturned.taskInfo.result);
        } else {
          setFileStatus('File received. Processing started…');
        }
      } else {
        const errorData = await response.json();
        setFileStatus((errorData && errorData.error) || 'Upload failed. Please try again.');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setFileStatus('Upload timed out. Please try again. Large files may take longer than 5 minutes.');
      } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        setFileStatus('Connection lost. Please check your network and try again.');
      } else {
        setFileStatus(`Upload error: ${error.message}`);
      }
    }
  };

  return (
    <div className="content-wrapper">
      <div className="main-section">
        <div className="upload-section">
          <form className="upload-form" onSubmit={handleSubmit}>
            <div className="file-input-group">
              <label htmlFor="file-upload" className="button button-primary">
                Choose ACES XML file
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".xml"
                name="file"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {fileName && <span className="selected-file">Selected file: {fileName}</span>}
            </div>
            <div className="email-input-group">
              <label htmlFor="email-adding" className="">
                Email address (we&apos;ll email you the download link)
              </label>
              <input
                id="email-adding"
                ref={emailInputRef}
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                autoComplete="email"
                placeholder="name@company.com"
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? 'email-help' : undefined}
                className={emailError ? 'input-error' : ''}
                required
              />
              {emailError && (
                <div id="email-help" className="helper-text error">
                  {emailError}
                </div>
              )}
            </div>
            <div className="consent-checkbox">
              <input
                type="checkbox"
                id="subscription"
                checked={isSubscribed}
                onChange={(e) => setIsSubscribed(e.target.checked)}
              />
              <label htmlFor="subscription">
                I have an active AutoCare subscription and understand that unauthorized use of AutoCare ACES
                files is prohibited.
              </label>
            </div>
            <div className="consent-checkbox">
              <input
                type="checkbox"
                id="privacy"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
              />
              <label htmlFor="privacy">
                We respect your privacy. Convermax uses your information to share relevant content, products,
                and services. You can unsubscribe anytime. For more information, check out our{' '}
                <a href="https://convermax.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </label>
            </div>
            <div className="button-group">
              <button
                className="button button-primary"
                type="submit"
                disabled={
                  !file ||
                  taskStatus.message === 'In Progress' ||
                  !isSubscribed ||
                  !privacyAccepted ||
                  !isValidEmail(email)
                }
              >
                Convert to CSV
              </button>
            </div>
          </form>
        </div>

        <div className="processing-section">
          {fileStatus && <div className="file-status" dangerouslySetInnerHTML={{ __html: fileStatus }}></div>}
          <ProgressBar
            percentage={taskStatus.percentage}
            message={getDisplayStatus(taskStatus.message)}
            disabled={!mainTaskId}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
